import React, { useState, useEffect } from 'react';
import { Database, Save, TestTube } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabaseClient';
import { createClient } from '@supabase/supabase-js';

interface DatabaseConfig {
  project_url: string;
  anon_key: string;
  service_role_key: string;
  jwt_secret: string;
  db_host: string;
  db_port: string;
  db_name: string;
  db_user: string;
  db_password: string;
}

export default function DatabaseSettings() {
  const [config, setConfig] = useState<DatabaseConfig>({
    project_url: '',
    anon_key: '',
    service_role_key: '',
    jwt_secret: '',
    db_host: '',
    db_port: '5432',
    db_name: '',
    db_user: '',
    db_password: ''
  });
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('supabase_config')
        .select('*')
        .limit(1)
        .single();

      if (error) {
        if (error.code !== 'PGRST116') {
          throw error;
        }
      }

      if (data) {
        setConfig(data);
      }
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const { data: existing } = await supabase
        .from('supabase_config')
        .select('id')
        .limit(1)
        .single();

      if (existing) {
        const { error } = await supabase
          .from('supabase_config')
          .update(config)
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('supabase_config')
          .insert([config]);

        if (error) throw error;
      }

      toast.success('Configuration sauvegardée avec succès');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const testClient = createClient(
        config.project_url,
        config.anon_key
      );

      const { data, error } = await testClient
        .from('users')
        .select('id')
        .limit(1);

      if (error) throw error;
      
      toast.success('Connexion établie avec succès');
    } catch (error) {
      console.error('Connection test failed:', error);
      toast.error('Échec de la connexion: Veuillez vérifier les paramètres');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center mb-6">
        <Database className="h-8 w-8 text-indigo-600 mr-3" />
        <h2 className="text-2xl font-bold text-gray-900">
          Configuration Base de Données
        </h2>
      </div>

      <div className="space-y-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Supabase Auto-Hébergé
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL du Projet
              </label>
              <input
                type="text"
                value={config.project_url}
                onChange={(e) => setConfig({ ...config, project_url: e.target.value })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="https://your-project.supabase.co"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clé Anonyme
              </label>
              <input
                type="text"
                value={config.anon_key}
                onChange={(e) => setConfig({ ...config, anon_key: e.target.value })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="your-anon-key"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clé Service Role
              </label>
              <input
                type="text"
                value={config.service_role_key}
                onChange={(e) => setConfig({ ...config, service_role_key: e.target.value })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="your-service-role-key"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                JWT Secret
              </label>
              <input
                type="text"
                value={config.jwt_secret}
                onChange={(e) => setConfig({ ...config, jwt_secret: e.target.value })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="your-jwt-secret"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hôte Base de Données
              </label>
              <input
                type="text"
                value={config.db_host}
                onChange={(e) => setConfig({ ...config, db_host: e.target.value })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="db.example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Port Base de Données
              </label>
              <input
                type="text"
                value={config.db_port}
                onChange={(e) => setConfig({ ...config, db_port: e.target.value })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="5432"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom Base de Données
              </label>
              <input
                type="text"
                value={config.db_name}
                onChange={(e) => setConfig({ ...config, db_name: e.target.value })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="postgres"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Utilisateur Base de Données
              </label>
              <input
                type="text"
                value={config.db_user}
                onChange={(e) => setConfig({ ...config, db_user: e.target.value })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="postgres"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de Passe Base de Données
              </label>
              <input
                type="password"
                value={config.db_password}
                onChange={(e) => setConfig({ ...config, db_password: e.target.value })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="••••••••"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button
              onClick={handleTest}
              disabled={testing}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <TestTube className="h-4 w-4 mr-2" />
              {testing ? 'Test en cours...' : 'Tester la connexion'}
            </button>
            <button
              onClick={handleSave}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Save className="h-4 w-4 mr-2" />
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}